import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle, Clock, AlertCircle, MessageSquare, Inbox, Plus, Search, ChevronDown,
  Mail, Phone, Calendar, Camera, FileText, ChevronLeft, ChevronRight,
  ExternalLink, Check, User, CheckSquare, Square, MinusSquare, X, Trash2, Building2,
  ArrowDownWideNarrow, ArrowUpWideNarrow, AlertTriangle
} from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import SearchBar from '../components/Common/SearchBar';
import Modal from '../components/Common/Modal';
import { getAllAnfragen, updateAnfrageStatus, bulkUpdateAnfragen, createAnfrage, deleteAnfrage, bulkDeleteAnfragen } from '../api/anfrageApi';
import { getAllKunden } from '../api/kundenApi';
import { objektApi } from '../api/objektApi';
import { getAllBenutzer } from '../api/benutzerApi';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  neu: { label: 'Neu', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  in_bearbeitung: { label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  abgeschlossen: { label: 'Erledigt', color: 'bg-green-100 text-green-700', icon: CheckCircle }
};

const AGENT_STATUS_CONFIG = {
  NEU: { label: 'Neu', color: 'bg-gray-100 text-gray-600' },
  INFO_SAMMELN: { label: 'Info sammeln', color: 'bg-blue-100 text-blue-600' },
  WARTE_TERMIN_AUSWAHL: { label: 'Warte auf Termin', color: 'bg-purple-100 text-purple-600' },
  TERMIN_BESTAETIGT: { label: 'Termin best.', color: 'bg-green-100 text-green-600' },
  WARTE_KVA_MANUELL: { label: 'KVA manuell', color: 'bg-orange-100 text-orange-600' },
  WARTE_KVA_BESTAETIGUNG: { label: 'KVA Bestätigung', color: 'bg-orange-100 text-orange-600' },
  KVA_ERSTELLT: { label: 'KVA erstellt', color: 'bg-teal-100 text-teal-600' },
  DATEN_AUFGENOMMEN: { label: 'Daten aufgen.', color: 'bg-gray-100 text-gray-600' },
  MANUELL: { label: 'Manuell', color: 'bg-red-100 text-red-600' },
  WARTE_MANUELL: { label: 'Warte manuell', color: 'bg-red-100 text-red-600' },
  ANGEBOT_ANGENOMMEN: { label: 'Angenommen', color: 'bg-green-100 text-green-600' },
  ABGESCHLOSSEN: { label: 'Abgeschlossen', color: 'bg-green-100 text-green-600' }
};

const KATEGORIE_CONFIG = {
  'Termin': { icon: Calendar, color: 'text-purple-500' },
  'Angebot': { icon: FileText, color: 'text-blue-500' },
  'Info': { icon: MessageSquare, color: 'text-gray-500' },
  'Rückruf': { icon: Phone, color: 'text-green-500' },
  'E-Mail': { icon: Mail, color: 'text-yellow-500' }
};

const ITEMS_PER_PAGE = 21;

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(d)) / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return `Heute, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  } else if (days === 1) {
    return `Gestern, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  } else if (days > 1 && days < 7) {
    return `vor ${days} Tagen`;
  }
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const SORT_STORAGE_PREFIX = 'anfragenSortOrder_';
const SORT_OPTIONS = [
  { value: 'createdDesc', label: 'Neueste erstellt', short: 'Neueste', field: 'erstelltAm', dir: 'desc' },
  { value: 'createdAsc',  label: 'Älteste erstellt', short: 'Älteste', field: 'erstelltAm', dir: 'asc' },
  { value: 'updatedDesc', label: 'Zuletzt geändert', short: 'Zul. geändert', field: 'aktualisiertAm', dir: 'desc' },
  { value: 'updatedAsc',  label: 'Zuerst geändert', short: 'Zuerst geändert', field: 'aktualisiertAm', dir: 'asc' },
];
const DEFAULT_SORT = 'createdDesc';
const normalizeSortValue = (v) => {
  // Backward-compat: altes 2-Werte-Schema übersetzen
  if (v === 'newest') return 'createdDesc';
  if (v === 'oldest') return 'createdAsc';
  return SORT_OPTIONS.some(o => o.value === v) ? v : null;
};

export default function AnfragenUebersicht() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = user?.organisation?.id || null;

  const [anfragen, setAnfragen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'alle');
  const [bearbeiterFilter, setBearbeiterFilter] = useState(searchParams.get('bearbeiter') || 'alle');
  const [benutzerList, setBenutzerList] = useState([]);
  const [sortOrder, setSortOrder] = useState(() => {
    // Initial aus localStorage (pro Organisation gespeichert). Fallback: createdDesc.
    if (typeof window === 'undefined' || !orgId) return DEFAULT_SORT;
    const stored = normalizeSortValue(window.localStorage.getItem(`${SORT_STORAGE_PREFIX}${orgId}`));
    return stored || DEFAULT_SORT;
  });
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortDropdownRef = useRef(null);

  // Hydrate aus localStorage sobald orgId verfügbar (falls beim Mount noch nicht da).
  useEffect(() => {
    if (!orgId) return;
    const stored = normalizeSortValue(window.localStorage.getItem(`${SORT_STORAGE_PREFIX}${orgId}`));
    if (stored && stored !== sortOrder) {
      setSortOrder(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Persistieren bei Änderung, org-scoped (siehe CLAUDE.md: Chat-localStorage-Cross-Org-Bug).
  useEffect(() => {
    if (!orgId) return;
    window.localStorage.setItem(`${SORT_STORAGE_PREFIX}${orgId}`, sortOrder);
  }, [orgId, sortOrder]);
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // null | { type: 'single', id } | { type: 'bulk' }
  const [deleting, setDeleting] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Kunden-Dropdown state
  const [kunden, setKunden] = useState([]);
  const [loadingKunden, setLoadingKunden] = useState(false);
  const [selectedKunde, setSelectedKunde] = useState(null);
  const [kundenSuche, setKundenSuche] = useState('');
  const [showKundenDropdown, setShowKundenDropdown] = useState(false);
  const kundenDropdownRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({ betreff: '', nachricht: '', kategorie: 'Info', objektId: '' });
  const [formErrors, setFormErrors] = useState({});

  // Objekte state (für Hausverwaltungs-Kunden)
  const [selectedKundeObjekte, setSelectedKundeObjekte] = useState([]);
  const [loadingObjekte, setLoadingObjekte] = useState(false);

  useEffect(() => {
    loadAnfragen();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') loadAnfragen();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Mitarbeiter-Liste einmalig laden für den Zuweisungs-Filter
  useEffect(() => {
    getAllBenutzer({ aktiv: 'true' })
      .then(data => setBenutzerList(data || []))
      .catch(err => console.error('Mitarbeiter-Liste konnte nicht geladen werden:', err));
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (kundenDropdownRef.current && !kundenDropdownRef.current.contains(e.target)) {
        setShowKundenDropdown(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)) {
        setShowSortDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAnfragen = async () => {
    try {
      setLoading(true);
      const data = await getAllAnfragen();
      setAnfragen(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Anfragen');
    } finally {
      setLoading(false);
    }
  };

  const loadKunden = async () => {
    if (kunden.length > 0) return;
    try {
      setLoadingKunden(true);
      const data = await getAllKunden();
      setKunden(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoadingKunden(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setFormData({ betreff: '', nachricht: '', kategorie: 'Info', objektId: '' });
    setFormErrors({});
    setSelectedKunde(null);
    setSelectedKundeObjekte([]);
    setKundenSuche('');
    setShowKundenDropdown(false);
    loadKunden();
  };

  // Auto-öffnen wenn Sidebar-Button in der Detail-Ansicht uns hierher
  // navigiert hat (?neu=1). Danach Query-Param wieder entfernen, damit
  // ein Reload nicht nochmal aufmacht.
  useEffect(() => {
    if (searchParams.get('neu') === '1') {
      handleOpenModal();
      const next = new URLSearchParams(searchParams);
      next.delete('neu');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormErrors({});
    setSelectedKunde(null);
    setKundenSuche('');
  };

  const gefilterteKunden = useMemo(() => {
    if (!kundenSuche.trim()) return kunden;
    const s = kundenSuche.toLowerCase();
    return kunden.filter(k =>
      k.name?.toLowerCase().includes(s) ||
      k.email?.toLowerCase().includes(s) ||
      k.telefon?.toLowerCase().includes(s)
    );
  }, [kunden, kundenSuche]);

  const handleKundeSelect = async (kunde) => {
    setSelectedKunde(kunde);
    setShowKundenDropdown(false);
    setKundenSuche('');
    setFormData(prev => ({ ...prev, objektId: '' }));
    if (formErrors.kundeId) {
      setFormErrors(prev => ({ ...prev, kundeId: '' }));
    }

    // Bei Hausverwaltungs-Kunden: Objekte laden
    if (kunde.kundenTyp === 'HAUSVERWALTUNG') {
      setLoadingObjekte(true);
      try {
        const res = await objektApi.getByKunde(kunde.id);
        setSelectedKundeObjekte(res.data || []);
      } catch (err) {
        setSelectedKundeObjekte([]);
      } finally {
        setLoadingObjekte(false);
      }
    } else {
      setSelectedKundeObjekte([]);
    }
  };

  const handleKundeRemove = () => {
    setSelectedKunde(null);
    setKundenSuche('');
    setSelectedKundeObjekte([]);
    setFormData(prev => ({ ...prev, objektId: '' }));
  };

  const handleCreateAnfrage = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!selectedKunde) errors.kundeId = 'Bitte wählen Sie einen Kunden aus';
    if (!formData.betreff.trim()) errors.betreff = 'Betreff ist erforderlich';
    if (!formData.nachricht.trim()) errors.nachricht = 'Nachricht ist erforderlich';
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSaving(true);
    try {
      const newAnfrage = await createAnfrage({
        kundeId: selectedKunde.id,
        betreff: formData.betreff,
        nachricht: formData.nachricht,
        kategorie: formData.kategorie,
        ...(formData.objektId && { objektId: formData.objektId })
      });
      toast.success('Anfrage erstellt');
      handleCloseModal();
      loadAnfragen();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Erstellen');
    } finally {
      setIsSaving(false);
    }
  };

  // Filter + Sortierung anwenden
  const filteredAnfragen = useMemo(() => {
    let filtered = anfragen;

    if (statusFilter === 'offen') {
      filtered = filtered.filter(a => a.status === 'neu' || a.status === 'in_bearbeitung');
    } else if (statusFilter === 'neu') {
      filtered = filtered.filter(a => a.status === 'neu');
    } else if (statusFilter === 'erledigt') {
      filtered = filtered.filter(a => a.status === 'abgeschlossen');
    }

    // Bearbeiter-Filter
    if (bearbeiterFilter === 'me' && user?.id) {
      filtered = filtered.filter(a => a.bearbeiterId === user.id);
    } else if (bearbeiterFilter === 'unassigned') {
      filtered = filtered.filter(a => !a.bearbeiterId);
    } else if (bearbeiterFilter !== 'alle') {
      filtered = filtered.filter(a => a.bearbeiterId === bearbeiterFilter);
    }

    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(a =>
        a.betreff?.toLowerCase().includes(s) ||
        a.nachricht?.toLowerCase().includes(s) ||
        a.kunde?.name?.toLowerCase().includes(s) ||
        a.kunde?.email?.toLowerCase().includes(s)
      );
    }

    const sortDef = SORT_OPTIONS.find(o => o.value === sortOrder) || SORT_OPTIONS[0];
    const { field, dir } = sortDef;
    const sorted = [...filtered].sort((a, b) => {
      // Fallback auf erstelltAm falls aktualisiertAm fehlt (Altdaten ohne updated-tracking)
      const ta = new Date(a[field] || a.erstelltAm).getTime();
      const tb = new Date(b[field] || b.erstelltAm).getTime();
      return dir === 'asc' ? ta - tb : tb - ta;
    });

    return sorted;
  }, [anfragen, statusFilter, searchTerm, sortOrder, bearbeiterFilter, user?.id]);

  // Pagination
  const totalPages = Math.ceil(filteredAnfragen.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedAnfragen = filteredAnfragen.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Clear selection when filter/search changes; bei Sort-Wechsel nur Page zurück.
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [statusFilter, searchTerm, bearbeiterFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortOrder]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== 'alle') params.set('status', statusFilter);
    if (searchTerm) params.set('search', searchTerm);
    if (bearbeiterFilter && bearbeiterFilter !== 'alle') params.set('bearbeiter', bearbeiterFilter);
    setSearchParams(params, { replace: true });
  }, [statusFilter, searchTerm, bearbeiterFilter]);

  const handleStatusChange = async (anfrageId, newStatus) => {
    setUpdatingIds(prev => new Set([...prev, anfrageId]));
    try {
      await updateAnfrageStatus(anfrageId, newStatus);
      setAnfragen(prev => prev.map(a =>
        a.id === anfrageId ? { ...a, status: newStatus } : a
      ));
      toast.success(newStatus === 'abgeschlossen' ? 'Als erledigt markiert' : 'Wieder geöffnet');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setUpdatingIds(prev => {
        const next = new Set(prev);
        next.delete(anfrageId);
        return next;
      });
    }
  };

  // Selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredAnfragen.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAnfragen.map(a => a.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedIds.size === 0) return;
    setBulkUpdating(true);
    try {
      const ids = Array.from(selectedIds);
      await bulkUpdateAnfragen(ids, newStatus);
      setAnfragen(prev => prev.map(a =>
        selectedIds.has(a.id) ? { ...a, status: newStatus } : a
      ));
      const label = newStatus === 'abgeschlossen' ? 'als erledigt markiert' : 'wieder geöffnet';
      toast.success(`${ids.length} Anfragen ${label}`);
      setSelectedIds(new Set());
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleDeleteSingle = async (id) => {
    setDeleting(true);
    try {
      await deleteAnfrage(id);
      setAnfragen(prev => prev.filter(a => a.id !== id));
      setSelectedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
      toast.success('Anfrage gelöscht');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Löschen');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      await bulkDeleteAnfragen(ids);
      setAnfragen(prev => prev.filter(a => !selectedIds.has(a.id)));
      toast.success(`${ids.length} Anfragen gelöscht`);
      setSelectedIds(new Set());
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Löschen');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  // Zähler
  const offeneCount = anfragen.filter(a => a.status === 'neu' || a.status === 'in_bearbeitung').length;
  const neueCount = anfragen.filter(a => a.status === 'neu').length;
  const erledigteCount = anfragen.filter(a => a.status === 'abgeschlossen').length;

  // Selection state for header checkbox
  const allOnPageSelected = paginatedAnfragen.length > 0 && paginatedAnfragen.every(a => selectedIds.has(a.id));
  const someSelected = selectedIds.size > 0;
  const allFilteredSelected = filteredAnfragen.length > 0 && selectedIds.size === filteredAnfragen.length;

  const kategorien = ['Termin', 'Angebot', 'Info', 'Rückruf'];

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-8 flex justify-center items-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 overflow-x-hidden">
        {/* Header - Kundenlisten-Stil */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold flex items-center gap-2">
              <Inbox className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
              Anfragen
            </h1>
            <p className="text-sm text-text-muted mt-1 sm:hidden">
              {filteredAnfragen.length} von {anfragen.length} Anfragen
            </p>
            {/* Mobile: Button unter Titel */}
            <div className="flex sm:hidden items-center gap-2 mt-3">
              <button
                onClick={handleOpenModal}
                className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark transition"
              >
                <Plus className="w-5 h-5" />
                Neue Anfrage
              </button>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <div className="text-sm text-text-muted hidden sm:block">
              {filteredAnfragen.length} von {anfragen.length} Anfragen
            </div>
            {/* Desktop: Button rechts */}
            <button
              onClick={handleOpenModal}
              className="hidden sm:flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark transition"
            >
              <Plus className="w-5 h-5" />
              Neue Anfrage
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6 space-y-3">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Suche nach Betreff, Kunde, E-Mail oder Nachricht..."
          />

          {/* Status-Filter Pills + Sort-Toggle */}
          <div className="flex gap-2 flex-wrap items-center">
            {[
              { value: 'alle', label: 'Alle', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200', active: 'bg-gray-700 text-white' },
              { value: 'offen', label: 'Offen', color: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100', active: 'bg-yellow-600 text-white', count: offeneCount },
              { value: 'neu', label: 'Neu', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100', active: 'bg-blue-600 text-white', count: neueCount },
              { value: 'erledigt', label: 'Erledigt', color: 'bg-green-50 text-green-700 hover:bg-green-100', active: 'bg-green-600 text-white', count: erledigteCount },
            ].map(f => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                  statusFilter === f.value ? f.active : f.color
                }`}
              >
                {f.label}
                {f.count !== undefined && (
                  <span className="ml-1.5 opacity-70">{f.count}</span>
                )}
              </button>
            ))}

            {/* Bearbeiter-Filter */}
            <select
              value={bearbeiterFilter}
              onChange={(e) => setBearbeiterFilter(e.target.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition focus:outline-none focus:ring-2 focus:ring-primary ${
                bearbeiterFilter !== 'alle'
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white border-border text-text hover:bg-gray-50'
              }`}
              title="Filter nach Zuständigkeit"
            >
              <option value="alle">Alle Mitarbeiter</option>
              {user?.id && <option value="me">Nur meine</option>}
              <option value="unassigned">Nicht zugewiesen</option>
              {benutzerList.length > 0 && (
                <optgroup label="Mitarbeiter">
                  {benutzerList.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            {(() => {
              const current = SORT_OPTIONS.find(o => o.value === sortOrder) || SORT_OPTIONS[0];
              const SortIcon = current.dir === 'asc' ? ArrowUpWideNarrow : ArrowDownWideNarrow;
              return (
                <div className="ml-auto relative" ref={sortDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowSortDropdown(v => !v)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-white border border-border text-text hover:bg-gray-50 transition"
                    title="Sortierung ändern"
                  >
                    <SortIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">{current.label}</span>
                    <span className="sm:hidden">{current.short}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showSortDropdown && (
                    <div className="absolute right-0 mt-1 w-56 bg-white border border-border rounded-lg shadow-lg z-20 py-1">
                      {SORT_OPTIONS.map(opt => {
                        const OptIcon = opt.dir === 'asc' ? ArrowUpWideNarrow : ArrowDownWideNarrow;
                        const active = opt.value === sortOrder;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => { setSortOrder(opt.value); setShowSortDropdown(false); }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition ${
                              active ? 'bg-primary/10 text-primary font-medium' : 'text-text hover:bg-gray-50'
                            }`}
                          >
                            <OptIcon className="w-4 h-4 shrink-0" />
                            <span className="flex-1">{opt.label}</span>
                            {active && <Check className="w-4 h-4 shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Bulk Action Bar - Desktop: inline bar, Mobile: sticky bottom bar */}
        {someSelected && (
          <>
            {/* Desktop Bulk Bar */}
            <div className="mb-4 hidden sm:flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-lg px-4 py-2.5">
              <button
                onClick={clearSelection}
                className="p-1 hover:bg-gray-200 rounded transition"
                title="Auswahl aufheben"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
              <span className="text-sm font-medium text-gray-700">
                {selectedIds.size} {selectedIds.size === 1 ? 'Anfrage' : 'Anfragen'} ausgewählt
              </span>
              {!allFilteredSelected && filteredAnfragen.length > selectedIds.size && (
                <button
                  onClick={toggleSelectAll}
                  className="text-sm text-primary hover:underline"
                >
                  Alle {filteredAnfragen.length} auswählen
                </button>
              )}
              <div className="ml-auto flex gap-2">
                <button
                  onClick={() => handleBulkStatusChange('abgeschlossen')}
                  disabled={bulkUpdating}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-green-600 text-white hover:bg-green-700 rounded-lg transition disabled:opacity-50"
                >
                  <Check className="w-4 h-4" />
                  Als erledigt markieren
                </button>
                <button
                  onClick={() => handleBulkStatusChange('neu')}
                  disabled={bulkUpdating}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition disabled:opacity-50"
                >
                  <AlertCircle className="w-4 h-4" />
                  Wieder öffnen
                </button>
                <button
                  onClick={() => setDeleteConfirm({ type: 'bulk' })}
                  disabled={bulkUpdating}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-red-600 text-white hover:bg-red-700 rounded-lg transition disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Löschen
                </button>
              </div>
            </div>

            {/* Mobile Sticky Bottom Bar - positioned above bottom nav */}
            <div className="sm:hidden fixed left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg px-3 py-2.5" style={{ bottom: 'calc(var(--bottom-nav-height, 64px) + var(--safe-area-bottom, 0px))' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={clearSelection}
                    className="p-1.5 hover:bg-gray-200 rounded transition"
                    title="Auswahl aufheben"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                  <span className="text-sm font-medium text-gray-700">
                    {selectedIds.size} ausgewählt
                  </span>
                </div>
                {!allFilteredSelected && filteredAnfragen.length > selectedIds.size && (
                  <button
                    onClick={toggleSelectAll}
                    className="text-xs text-primary hover:underline"
                  >
                    Alle {filteredAnfragen.length}
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleBulkStatusChange('abgeschlossen')}
                  disabled={bulkUpdating}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-sm font-medium bg-green-600 text-white hover:bg-green-700 rounded-lg transition disabled:opacity-50"
                >
                  <Check className="w-4 h-4" />
                  Erledigt
                </button>
                <button
                  onClick={() => handleBulkStatusChange('neu')}
                  disabled={bulkUpdating}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition disabled:opacity-50"
                >
                  <AlertCircle className="w-4 h-4" />
                  Öffnen
                </button>
                <button
                  onClick={() => setDeleteConfirm({ type: 'bulk' })}
                  disabled={bulkUpdating}
                  className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-sm font-medium bg-red-600 text-white hover:bg-red-700 rounded-lg transition disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Löschen
                </button>
              </div>
            </div>
            {/* Spacer to prevent content from being hidden behind sticky bar on mobile */}
            <div className="sm:hidden h-24" />
          </>
        )}

        {/* Anfragen Liste */}
        {filteredAnfragen.length === 0 ? (
          <div className="bg-surface rounded-card shadow-card p-8 text-center">
            <p className="text-text-muted">
              {searchTerm ? 'Keine Anfragen gefunden.' : 'Keine Anfragen vorhanden.'}
            </p>
          </div>
        ) : (
          <div className="bg-surface rounded-card shadow-card overflow-hidden">
            {/* Tabelle Header */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-border text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-1 flex items-center">
                <button onClick={toggleSelectAll} className="p-0.5 hover:bg-gray-200 rounded transition">
                  {allOnPageSelected && filteredAnfragen.length > 0 ? (
                    <CheckSquare className="w-4 h-4 text-primary" />
                  ) : someSelected ? (
                    <MinusSquare className="w-4 h-4 text-primary" />
                  ) : (
                    <Square className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              </div>
              <div className="col-span-3">Anfrage</div>
              <div className="col-span-2">Kunde</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Datum</div>
              <div className="col-span-2 text-right">Aktionen</div>
            </div>

            {/* Zeilen */}
            <div className="divide-y divide-border">
              {paginatedAnfragen.map((anfrage) => {
                const statusConf = STATUS_CONFIG[anfrage.status] || STATUS_CONFIG.neu;
                const agentConf = AGENT_STATUS_CONFIG[anfrage.agentStatus] || null;
                const katConf = KATEGORIE_CONFIG[anfrage.kategorie] || null;
                const KatIcon = katConf?.icon || MessageSquare;
                const isUpdating = updatingIds.has(anfrage.id);
                const isSelected = selectedIds.has(anfrage.id);

                return (
                  <div
                    key={anfrage.id}
                    onClick={(e) => {
                      // Mobile: ganzen Row klickbar machen → Anfrage direkt öffnen
                      if (window.innerWidth < 768) {
                        if (e.target.closest('button') || e.target.closest('a')) return;
                        navigate(`/anfragen/${anfrage.id}`);
                      }
                    }}
                    className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 py-3 hover:bg-gray-50 transition items-center cursor-pointer md:cursor-default ${
                      isSelected ? 'bg-primary/5' : ''
                    }`}
                  >
                    {/* Checkbox */}
                    <div className="hidden md:flex col-span-1 items-center">
                      <button
                        onClick={() => toggleSelect(anfrage.id)}
                        className="p-0.5 hover:bg-gray-200 rounded transition"
                      >
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4 text-primary" />
                        ) : (
                          <Square className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>

                    {/* Anfrage Info */}
                    <div className="col-span-3 flex items-start gap-3 min-w-0">
                      {/* Mobile: checkbox inline */}
                      <button
                        onClick={() => toggleSelect(anfrage.id)}
                        className="md:hidden p-0.5 mt-0.5 flex-shrink-0"
                      >
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4 text-primary" />
                        ) : (
                          <Square className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                      <div className={`mt-0.5 flex-shrink-0 hidden md:block ${katConf?.color || 'text-gray-400'}`}>
                        <KatIcon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          {anfrage.istNotfall && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-red-600 text-white rounded shrink-0"
                              title="Notdienst — Anruf wurde an den Notdienst weitergeleitet"
                            >
                              <AlertTriangle className="w-2.5 h-2.5" />
                              Notdienst
                            </span>
                          )}
                          <Link
                            to={`/anfragen/${anfrage.id}`}
                            className={`font-medium text-sm hover:text-primary truncate block ${
                              anfrage.istNotfall ? 'text-red-900' : 'text-text'
                            }`}
                          >
                            {(anfrage.betreff || '').replace(/^🚨\s*NOTFALL\s*—\s*/i, '').trim() || '—'}
                          </Link>
                        </div>
                        <p className="text-xs text-text-muted truncate mt-0.5">
                          {anfrage.nachricht?.substring(0, 80)}
                          {anfrage.nachricht?.length > 80 ? '...' : ''}
                        </p>
                        <div className="flex gap-2 mt-1 flex-wrap">
                          {anfrage._count?.emailKonversationen > 0 && (
                            <span className="text-xs text-gray-400 flex items-center gap-0.5">
                              <Mail className="w-3 h-3" /> {anfrage._count.emailKonversationen}
                            </span>
                          )}
                          {anfrage._count?.fotos > 0 && (
                            <span className="text-xs text-gray-400 flex items-center gap-0.5">
                              <Camera className="w-3 h-3" /> {anfrage._count.fotos}
                            </span>
                          )}
                          {anfrage._count?.kostenvoranschlaege > 0 && (
                            <span className="text-xs text-gray-400 flex items-center gap-0.5">
                              <FileText className="w-3 h-3" /> {anfrage._count.kostenvoranschlaege}
                            </span>
                          )}
                          {anfrage.bearbeiter && (
                            <span
                              className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium truncate max-w-[140px]"
                              title={`Zugewiesen an ${anfrage.bearbeiter.name}`}
                            >
                              {anfrage.bearbeiter.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Kunde */}
                    <div className="col-span-2 min-w-0">
                      {anfrage.kunde ? (
                        <Link
                          to={`/kunden/${anfrage.kunde.id}`}
                          className="text-sm text-text hover:text-primary truncate block"
                        >
                          {anfrage.kunde.name}
                        </Link>
                      ) : (
                        <span className="text-sm text-text-muted">-</span>
                      )}
                      {anfrage.objekt && (
                        <p className="text-xs text-purple-600 truncate flex items-center gap-1">
                          <span className="inline-block w-1.5 h-1.5 bg-purple-400 rounded-full flex-shrink-0"></span>
                          {anfrage.objekt.name || anfrage.objekt.strasse}
                        </p>
                      )}
                      {!anfrage.objekt && anfrage.kunde?.email && (
                        <p className="text-xs text-text-muted truncate">{anfrage.kunde.email}</p>
                      )}
                    </div>

                    {/* Status Badges */}
                    <div className="col-span-2 flex flex-col gap-1">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full w-fit ${statusConf.color}`}>
                        <statusConf.icon className="w-3 h-3" />
                        {statusConf.label}
                      </span>
                      {agentConf && anfrage.agentStatus && anfrage.agentStatus !== 'NEU' && (
                        <span className={`inline-flex text-xs px-2 py-0.5 rounded-full w-fit ${agentConf.color}`}>
                          {agentConf.label}
                        </span>
                      )}
                    </div>

                    {/* Datum — zeigt das Feld, nach dem aktuell sortiert wird */}
                    <div className="col-span-2">
                      {(() => {
                        const sortDef = SORT_OPTIONS.find(o => o.value === sortOrder) || SORT_OPTIONS[0];
                        const showUpdated = sortDef.field === 'aktualisiertAm';
                        const primary = showUpdated ? anfrage.aktualisiertAm : anfrage.erstelltAm;
                        const label = showUpdated ? 'Geändert' : 'Erstellt';
                        return (
                          <>
                            <p className="text-sm text-text-muted">
                              <span className="text-xs text-gray-400 mr-1">{label}:</span>
                              {formatDate(primary)}
                            </p>
                            {anfrage.kategorie && (
                              <p className="text-xs text-text-muted">{anfrage.kategorie}</p>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    {/* Aktionen */}
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      {anfrage.status !== 'abgeschlossen' ? (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleStatusChange(anfrage.id, 'abgeschlossen');
                          }}
                          disabled={isUpdating}
                          className="flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 rounded-lg transition disabled:opacity-50"
                          title="Als erledigt markieren"
                        >
                          <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          <span className="hidden lg:inline">Erledigt</span>
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleStatusChange(anfrage.id, 'neu');
                          }}
                          disabled={isUpdating}
                          className="flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 text-xs font-medium bg-gray-50 text-gray-600 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
                          title="Wieder öffnen"
                        >
                          <AlertCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          <span className="hidden lg:inline">Öffnen</span>
                        </button>
                      )}
                      {anfrage.kunde && (
                        <Link
                          to={`/kunden/${anfrage.kunde.id}`}
                          className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 hover:text-primary hover:bg-gray-50 rounded-lg transition"
                          title="Zum Kunden"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Link>
                      )}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setDeleteConfirm({ type: 'single', id: anfrage.id });
                        }}
                        className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Löschen"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pagination - Kundenlisten-Stil */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 bg-surface rounded-card shadow-card px-4 sm:px-6 py-4">
            <div className="text-sm text-text-muted">
              Zeige {startIndex + 1} - {Math.min(startIndex + ITEMS_PER_PAGE, filteredAnfragen.length)} von {filteredAnfragen.length} Anfragen
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Zurück</span>
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                  .map((page, idx, arr) => (
                    <span key={page} className="flex items-center">
                      {idx > 0 && arr[idx - 1] !== page - 1 && (
                        <span className="px-1 sm:px-2 text-text-muted">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-2 sm:px-3 py-2 text-sm rounded-button transition ${
                          currentPage === page
                            ? 'bg-primary text-white'
                            : 'hover:bg-gray-50 border border-border'
                        }`}
                      >
                        {page}
                      </button>
                    </span>
                  ))}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1 px-2 sm:px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <span className="hidden sm:inline">Weiter</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Modal: Neue Anfrage */}
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title="Neue Anfrage"
        >
          <form onSubmit={handleCreateAnfrage} className="space-y-4">
            {/* Kunde auswählen mit Suchfunktion */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Kunde <span className="text-danger">*</span>
              </label>

              <div className="relative" ref={kundenDropdownRef}>
                {selectedKunde ? (
                  <div className={`flex items-center justify-between w-full px-3 sm:px-4 py-2.5 border rounded-lg bg-gray-50 ${
                    formErrors.kundeId ? 'border-danger' : 'border-border'
                  }`}>
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-sm sm:text-base block truncate">{selectedKunde.name}</span>
                      {selectedKunde.email && (
                        <span className="text-text-muted text-xs sm:text-sm block truncate">{selectedKunde.email}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleKundeRemove}
                      className="p-1.5 hover:bg-gray-200 rounded transition ml-2 shrink-0"
                      disabled={isSaving}
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={kundenSuche}
                      onChange={(e) => {
                        setKundenSuche(e.target.value);
                        setShowKundenDropdown(true);
                      }}
                      onFocus={() => setShowKundenDropdown(true)}
                      placeholder="Kunde suchen..."
                      className={`w-full pl-10 pr-10 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base ${
                        formErrors.kundeId ? 'border-danger' : 'border-border'
                      }`}
                      disabled={isSaving || loadingKunden}
                    />
                    <button
                      type="button"
                      onClick={() => setShowKundenDropdown(!showKundenDropdown)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      disabled={isSaving || loadingKunden}
                    >
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showKundenDropdown ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                )}

                {/* Dropdown Liste */}
                {showKundenDropdown && !selectedKunde && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {loadingKunden ? (
                      <div className="px-4 py-3 text-text-muted text-center">
                        Lädt Kunden...
                      </div>
                    ) : gefilterteKunden.length === 0 ? (
                      <div className="px-4 py-3 text-text-muted text-center">
                        {kundenSuche ? 'Kein Kunde gefunden' : 'Keine Kunden vorhanden'}
                      </div>
                    ) : (
                      gefilterteKunden.map(kunde => (
                        <button
                          key={kunde.id}
                          type="button"
                          onClick={() => handleKundeSelect(kunde)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-border last:border-b-0 transition"
                        >
                          <div className="font-medium">{kunde.name}</div>
                          <div className="text-sm text-text-muted flex gap-3">
                            {kunde.email && <span>{kunde.email}</span>}
                            {kunde.telefon && <span>{kunde.telefon}</span>}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {formErrors.kundeId && (
                <p className="text-danger text-sm mt-1">{formErrors.kundeId}</p>
              )}
            </div>

            {/* Betreff */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Betreff <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                value={formData.betreff}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, betreff: e.target.value }));
                  if (formErrors.betreff) setFormErrors(prev => ({ ...prev, betreff: '' }));
                }}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                  formErrors.betreff ? 'border-danger' : 'border-border'
                }`}
                placeholder="z.B. Kostenvoranschlag Fensterscheibe"
                disabled={isSaving}
              />
              {formErrors.betreff && (
                <p className="text-danger text-sm mt-1">{formErrors.betreff}</p>
              )}
            </div>

            {/* Kategorie */}
            <div>
              <label className="block text-sm font-medium mb-2">Kategorie</label>
              <div className="flex gap-2 flex-wrap">
                {kategorien.map((kat) => (
                  <button
                    key={kat}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, kategorie: kat }))}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      formData.kategorie === kat
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    disabled={isSaving}
                  >
                    {kat}
                  </button>
                ))}
              </div>
            </div>

            {/* Objekt-Auswahl (nur bei Hausverwaltungs-Kunden) */}
            {selectedKunde?.kundenTyp === 'HAUSVERWALTUNG' && (
              <div>
                <label className="block text-sm font-medium mb-2 flex items-center gap-1.5">
                  <Building2 className="w-4 h-4 text-purple-500" />
                  Objekt
                </label>
                {loadingObjekte ? (
                  <div className="text-sm text-text-muted py-2">Objekte werden geladen...</div>
                ) : selectedKundeObjekte.length > 0 ? (
                  <select
                    value={formData.objektId}
                    onChange={(e) => setFormData(prev => ({ ...prev, objektId: e.target.value }))}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    disabled={isSaving}
                  >
                    <option value="">Kein Objekt zugeordnet</option>
                    {selectedKundeObjekte.filter(o => o.aktiv !== false).map(obj => (
                      <option key={obj.id} value={obj.id}>
                        {obj.name || obj.strasse}{obj.ort ? `, ${obj.ort}` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-text-muted py-1">Keine Objekte vorhanden</p>
                )}
              </div>
            )}

            {/* Nachricht */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Nachricht <span className="text-danger">*</span>
              </label>
              <textarea
                value={formData.nachricht}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, nachricht: e.target.value }));
                  if (formErrors.nachricht) setFormErrors(prev => ({ ...prev, nachricht: '' }));
                }}
                rows="5"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                  formErrors.nachricht ? 'border-danger' : 'border-border'
                }`}
                placeholder="Beschreiben Sie die Anfrage..."
                disabled={isSaving}
              />
              {formErrors.nachricht && (
                <p className="text-danger text-sm mt-1">{formErrors.nachricht}</p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-primary text-white py-3 rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSaving}
              >
                {isSaving ? 'Wird erstellt...' : 'Erstellen'}
              </button>
              <button
                type="button"
                onClick={handleCloseModal}
                className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition disabled:opacity-50"
                disabled={isSaving}
              >
                Abbrechen
              </button>
            </div>
          </form>
        </Modal>

        {/* Lösch-Bestätigungsdialog */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold">
                  {deleteConfirm.type === 'bulk'
                    ? `${selectedIds.size} Anfragen löschen?`
                    : 'Anfrage löschen?'}
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                {deleteConfirm.type === 'bulk'
                  ? `${selectedIds.size} ausgewählte Anfragen und alle zugehörigen Daten (Kostenvoranschläge, E-Mail-Konversationen etc.) werden unwiderruflich gelöscht.`
                  : 'Diese Anfrage und alle zugehörigen Daten (Kostenvoranschläge, E-Mail-Konversationen etc.) werden unwiderruflich gelöscht.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (deleteConfirm.type === 'bulk') {
                      handleBulkDelete();
                    } else {
                      handleDeleteSingle(deleteConfirm.id);
                    }
                  }}
                  disabled={deleting}
                  className="flex-1 bg-red-600 text-white py-2.5 rounded-lg font-medium hover:bg-red-700 transition disabled:opacity-50"
                >
                  {deleting ? 'Wird gelöscht...' : 'Endgültig löschen'}
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition disabled:opacity-50"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
